import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'babychic-scroll:';
const RESTORE_TIMEOUT_MS = 4000;

function readPosition(key: string): number | null {
  try {
    const value = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return value === null ? null : Number(value);
  } catch {
    return null;
  }
}

function writePosition(key: string, value: number) {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(Math.round(value)));
  } catch {
    return;
  }
}

function scrollInstantly(top: number) {
  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, top);
  document.body.scrollTop = top;
  root.style.scrollBehavior = previousBehavior;
}

export function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef<string | null>(null);
  const handledKeyRef = useRef<string | null>(null);
  const cancelRestoreRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => writePosition(key, window.scrollY));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [key]);

  useEffect(() => {
    if (handledKeyRef.current === key) return;
    const isFirstRender = handledKeyRef.current === null;
    const pathnameChanged = previousPathnameRef.current !== pathname;
    handledKeyRef.current = key;
    previousPathnameRef.current = pathname;
    if (isFirstRender) return;

    cancelRestoreRef.current?.();
    cancelRestoreRef.current = null;

    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    const saved = navigationType === 'POP' ? readPosition(key) : null;
    if (!saved || saved <= 0) {
      if (!pathnameChanged) return;
      scrollInstantly(0);
      requestAnimationFrame(() => scrollInstantly(0));
      return;
    }

    let frame = 0;
    const startedAt = performance.now();
    const stop = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
      if (cancelRestoreRef.current === stop) cancelRestoreRef.current = null;
    };
    const tryRestore = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= saved || performance.now() - startedAt > RESTORE_TIMEOUT_MS) {
        scrollInstantly(Math.min(saved, Math.max(maxScroll, 0)));
        stop();
        return;
      }
      frame = requestAnimationFrame(tryRestore);
    };
    cancelRestoreRef.current = stop;
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    frame = requestAnimationFrame(tryRestore);
  }, [pathname, search, hash, key, navigationType]);

  return null;
}
